import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import persistStorage from "redux-persist/lib/storage";

import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { clearSelection } from "@redux/slices/tenantSlice";
import { clearNetworkEventsActiveCount } from "@redux/slices/networkAlertsSlice";
import { getLogoutRedirectUri } from "@axios-config/authRedirect";
import { useTheme } from "../../../../theme/components/ThemeProvider";

import { ReactComponent as SunIcon } from "@assets/sun.svg";
import { ReactComponent as MoonIcon } from "@assets/moon.svg";
import { ReactComponent as ProfileIcon } from "@assets/profile-circle.svg";
import { ReactComponent as IconDisplay } from "@assets/Icons/IconDisplay.svg";
import { ReactComponent as IconSettings } from "@assets/Icons/IconSettings.svg";
import { ReactComponent as IconLogout } from "@assets/Icons/IconLogout.svg";
import {
  FOCUS_RING,
  ROW_GAP,
  ROW_ICON,
  ROW_ICON_STANDALONE,
  ROW_PADDING,
  ROW_RADIUS,
  ROW_TEXT,
  TRANSITION_COLORS,
} from "../../SideMenu/sideMenuDensity";
import { getAccountScopeLine } from "../../SideMenu/sideMenuVisibility";
import { profileAreaItem } from "../../SideMenu/MenuData";

export type AccountPanelProps = {
  /** Brand wordmark for the panel's foot. */
  logoSrc: string;
  onLogoError: () => void;
  /**
   * Whether the Admin center row renders at all — `canAccessAdminCenter`
   * upstream (GLOBAL_ADMIN or the email whitelist), decided there so the rule
   * is stated once (CTX-583). This panel never recomputes the gate; a second
   * reading of it here is exactly how the two would drift apart.
   */
  showAdminCenterRow: boolean;
  /** Opens the Admin center panel (reuses SideMenu's handler). */
  onOpenAdminCenter: (e: React.MouseEvent) => void;
  /**
   * Dismisses whichever shell is hosting the panel — the rail dropdown, the
   * mobile bottom sheet, or the header menu. Log out deliberately does not call
   * it: the redirect is leaving the page anyway, and closing first would animate
   * the panel shut over a screen that is already going away.
   */
  onDismiss: () => void;
};

/**
 * Inline light/dark segmented control (the "Display" row) styled as a switch:
 * a gray track with an elevated white "thumb" on the active option. Icons use
 * `[&_path]:fill-current` so the active one picks up the brand (teal) color.
 */
function DisplayToggle() {
  const { theme, setTheme } = useTheme();
  const cell = (active: boolean) =>
    `flex h-6 w-8 items-center justify-center rounded ${TRANSITION_COLORS} ${FOCUS_RING} ${
      active
        ? "bg-semantic-bg-primary text-semantic-text-brand shadow-sm"
        : "text-semantic-text-secondary hover:text-semantic-text-primary"
    }`;
  return (
    <div className="flex items-center gap-1 rounded-lg bg-semantic-bg-secondary p-1">
      <button
        type="button"
        aria-label="Light theme"
        onClick={() => setTheme("theme-light")}
        className={cell(theme === "theme-light")}
      >
        <SunIcon className={`${ROW_ICON_STANDALONE} [&_path]:fill-current`} />
      </button>
      <button
        type="button"
        aria-label="Dark theme"
        onClick={() => setTheme("theme-dark")}
        className={cell(theme === "theme-dark")}
      >
        <MoonIcon className={`${ROW_ICON_STANDALONE} [&_path]:fill-current`} />
      </button>
    </div>
  );
}

/**
 * Who the user is and what they can do about it: identity, the Display (theme)
 * toggle, Admin center and Log out, over the CTX brand foot.
 *
 * One component, two hosts (CTX-635). It is the body of the rail's account card
 * minus the product list — picking a product is navigation, not identity, so
 * that list stays with the card in the rail (`SideMenuAccountCard`) while this
 * panel is what the header's account control opens too (CTX-636).
 */
export default function AccountPanel({
  logoSrc,
  onLogoError,
  showAdminCenterRow,
  onOpenAdminCenter,
  onDismiss,
}: AccountPanelProps) {
  const dispatch = useAppDispatch();
  const { logout } = useAuth0();

  const navigate = useNavigate();
  const userProfile = useAppSelector((s) => s.userProfile);
  const userName = `${userProfile.firstName ?? ""} ${
    userProfile.lastName ?? ""
  }`.trim();
  const userEmail = userProfile.email;
  /* "Partner · CCC.123" (CTX-335), replacing the bare tenant display name: the
     name alone did not say what the user *is* in it, and the id is what support
     asks for. Null for an unrecognised tenant type — see the helper. */
  const accountScopeLine = getAccountScopeLine({
    tenantType: userProfile.TenantType,
    tenantCode: userProfile.Tenant?.customerId,
  });

  /* Order is the contract, not an implementation detail: the tenant selection,
     the persisted root and the alert counter all have to be gone BEFORE Auth0
     navigates away, or the next session boots holding the previous user's
     tenant. Covered by `AccountPanel.wiring.test.tsx`. */
  const handleLogout = () => {
    dispatch(clearSelection());
    persistStorage.removeItem("persist:root");
    dispatch(clearNetworkEventsActiveCount());
    logout({ logoutParams: { returnTo: getLogoutRedirectUri() } });
  };

  /* Panel rows follow the same scale as the trigger and the nav — otherwise
     opening the card jumps back to the pre-CTX-242 14px. */
  const iconClass = `${ROW_ICON} text-semantic-text-secondary`;
  const rowClass = `flex w-full items-center ${ROW_GAP} ${ROW_RADIUS} ${ROW_PADDING} text-left ${ROW_TEXT} text-semantic-text-secondary ${TRANSITION_COLORS} ${FOCUS_RING} hover:bg-semantic-state-active hover:text-semantic-text-brand`;

  return (
    <>
      {/* Identity. The hairline that separates this from whatever sits above
          belongs to the host, not here — the rail stacks the panel under its
          product list, the header opens it against nothing. */}
      <div className="flex items-start gap-2 px-4 pt-3">
        {/* `mt-0.5` centres the glyph on the name's cap height — the block below
            it is three lines, so aligning to the box would sit it too high. */}
        <ProfileIcon className={`mt-0.5 ${ROW_ICON} shrink-0 text-semantic-text-secondary`} />
        <div className="min-w-0">
          <p className={`truncate ${ROW_TEXT} font-semibold text-semantic-text-primary`}>
            {userName}
          </p>
          {/* `truncate` needs the `min-w-0` on the wrapper above to engage — a
              long address is cut with an ellipsis rather than widening the panel
              past the rail (CTX-335 AC). */}
          {userEmail && (
            <p
              className="truncate text-[10px] text-semantic-text-secondary"
              title={userEmail}
            >
              {userEmail}
            </p>
          )}
          {accountScopeLine && (
            <p className="truncate text-[10px] text-semantic-text-secondary">
              {accountScopeLine}
            </p>
          )}
        </div>
      </div>

      {/* Display (theme) */}
      <div className="flex items-center justify-between px-4 py-2">
        <span className={`flex items-center ${ROW_GAP} ${ROW_TEXT} text-semantic-text-secondary`}>
          {/* The `-ml-[3px]` pull existed to cancel the whitespace a 20px render
              of this asset carried; at the row size it lines up on its own. */}
          <IconDisplay className={`${ROW_ICON} shrink-0 [&_path]:fill-current`} />
          Display
        </span>
        <DisplayToggle />
      </div>

      {/* Admin center + Log out. The divider is what makes this read as its own group
          rather than as two more rows of the Display block above it — the panel is
          grouped by hairlines throughout (CTX-335). */}
      <div className="border-t border-semantic-border-secondary" />
      <div className="px-2 py-2">
        {/* "Admin center", not "Settings" (CTX-583): everything a non-admin
            used to come here for is a rail row now, so the label finally
            describes what the panel behind it actually holds. */}
        {/* Profile opens this group because it is the one row here that is
            about the person the panel just named, rather than about the tenant
            — the rail carries everything tenant-shaped. Label, path and icon
            come from `profileAreaItem` so this row and the reachability check
            cannot describe different destinations. */}
        <button
          type="button"
          className={rowClass}
          onClick={() => {
            if (profileAreaItem.path) navigate(profileAreaItem.path);
            onDismiss();
          }}
        >
          {profileAreaItem.Icon && (
            <profileAreaItem.Icon className={iconClass} aria-hidden />
          )}
          <span>{profileAreaItem.label}</span>
        </button>
        {showAdminCenterRow && (
          <button
            type="button"
            className={rowClass}
            onClick={(e) => {
              onOpenAdminCenter(e);
              onDismiss();
            }}
          >
            <IconSettings className={iconClass} />
            <span>Admin center</span>
          </button>
        )}
        <button type="button" className={rowClass} onClick={handleLogout}>
          <IconLogout className={iconClass} />
          <span>Log out</span>
        </button>
      </div>

      {/* Foot: small left-aligned wordmark over its tagline (CTX-335). The
          wordmark is held at 16px — half the trigger's — because down here it is
          a signature rather than a control; at the previous 24px it was the
          heaviest thing in the panel and pulled the eye past Log out. */}
      <div className="flex flex-col items-start gap-0.5 border-t border-semantic-border-secondary bg-semantic-bg-secondary px-4 py-2">
        <img
          src={logoSrc}
          alt="Brand logo"
          className="max-h-4 w-auto object-contain text-semantic-text-primary"
          onError={onLogoError}
        />
        <span className="text-[10px] leading-none text-semantic-text-secondary">
          Cloud Transit Xchange
        </span>
      </div>
    </>
  );
}
